import { Injectable } from '@angular/core';
import { signal } from '@angular/core';
import { NzTableSortOrder } from 'ng-zorro-antd/table';
import { LocalStorageService } from './local-storage.service';

export type CmsTableStoredFilters = Record<string, (string | number | boolean)[]>;
export type CmsTableStoredSortOrders = Record<string, NzTableSortOrder>;

export function areCmsTableFiltersEqual(a: CmsTableStoredFilters, b: CmsTableStoredFilters): boolean {
	const aKeys = Object.keys(a).sort();
	const bKeys = Object.keys(b).sort();

	if (aKeys.length !== bKeys.length) {
		return false;
	}

	for (let i = 0; i < aKeys.length; i += 1) {
		if (aKeys[i] !== bKeys[i]) {
			return false;
		}

		const aValues = a[aKeys[i]] || [];
		const bValues = b[bKeys[i]] || [];

		if (aValues.length !== bValues.length) {
			return false;
		}

		for (let j = 0; j < aValues.length; j += 1) {
			if (aValues[j] !== bValues[j]) {
				return false;
			}
		}
	}

	return true;
}

export function areCmsTableSortOrdersEqual(a: CmsTableStoredSortOrders, b: CmsTableStoredSortOrders): boolean {
	const aKeys = Object.keys(a).sort();
	const bKeys = Object.keys(b).sort();

	if (aKeys.length !== bKeys.length) {
		return false;
	}

	for (let i = 0; i < aKeys.length; i += 1) {
		if (aKeys[i] !== bKeys[i]) {
			return false;
		}

		if (a[aKeys[i]] !== b[bKeys[i]]) {
			return false;
		}
	}

	return true;
}

@Injectable({
	providedIn: 'root',
})
export class CmsTableService {
	private readonly filtersByTableKey = signal<Record<string, CmsTableStoredFilters>>({});
	private readonly sortOrdersByTableKey = signal<Record<string, CmsTableStoredSortOrders>>({});
	private readonly pageSizeByTableKey = signal<Record<string, number>>({});
	private readonly pageIndexByTableKey = signal<Record<string, number>>({});

	constructor(private localStorageService: LocalStorageService) {
		this.pageSizeByTableKey.set(this.localStorageService.getCmsTablePageSizes());
	}

	public getSelectedFilters(tableKey: string): CmsTableStoredFilters {
		const allFilters = this.filtersByTableKey();
		const selectedFilters = allFilters[tableKey];

		if (!selectedFilters) {
			return {};
		}

		return this.cloneFilters(selectedFilters);
	}

	public setSelectedFilters(tableKey: string, filters: CmsTableStoredFilters): void {
		const nextFilters = this.cloneFilters(filters);
		const allFilters = this.filtersByTableKey();
		const currentFilters = allFilters[tableKey];

		if (currentFilters && areCmsTableFiltersEqual(currentFilters, nextFilters)) {
			return;
		}

		this.filtersByTableKey.set({
			...allFilters,
			[tableKey]: nextFilters,
		});
	}

	public clearSelectedFilters(tableKey: string): void {
		const allFilters = this.filtersByTableKey();

		if (!(tableKey in allFilters)) {
			return;
		}

		const nextFilters = { ...allFilters };
		delete nextFilters[tableKey];
		this.filtersByTableKey.set(nextFilters);
	}

	public getSelectedSortOrders(tableKey: string): CmsTableStoredSortOrders {
		const allSortOrders = this.sortOrdersByTableKey();
		const selectedSortOrders = allSortOrders[tableKey];

		if (!selectedSortOrders) {
			return {};
		}

		return { ...selectedSortOrders };
	}

	public setSelectedSortOrders(tableKey: string, sortOrders: CmsTableStoredSortOrders): void {
		const nextSortOrders = { ...sortOrders };
		const allSortOrders = this.sortOrdersByTableKey();
		const currentSortOrders = allSortOrders[tableKey];

		if (currentSortOrders && areCmsTableSortOrdersEqual(currentSortOrders, nextSortOrders)) {
			return;
		}

		this.sortOrdersByTableKey.set({
			...allSortOrders,
			[tableKey]: nextSortOrders,
		});
	}

	public clearSelectedSortOrders(tableKey: string): void {
		const allSortOrders = this.sortOrdersByTableKey();

		if (!(tableKey in allSortOrders)) {
			return;
		}

		const nextSortOrders = { ...allSortOrders };
		delete nextSortOrders[tableKey];
		this.sortOrdersByTableKey.set(nextSortOrders);
	}

	public getSelectedPageSize(tableKey: string): number | undefined {
		return this.pageSizeByTableKey()[tableKey];
	}

	public setSelectedPageSize(tableKey: string, pageSize: number): void {
		const allPageSizes = this.pageSizeByTableKey();

		if (allPageSizes[tableKey] === pageSize) {
			return;
		}

		const nextPageSizes = {
			...allPageSizes,
			[tableKey]: pageSize,
		};

		this.pageSizeByTableKey.set(nextPageSizes);
		this.localStorageService.setCmsTablePageSizes(nextPageSizes);
	}

	public clearSelectedPageSize(tableKey: string): void {
		const allPageSizes = this.pageSizeByTableKey();

		if (!(tableKey in allPageSizes)) {
			return;
		}

		const nextPageSizes = { ...allPageSizes };
		delete nextPageSizes[tableKey];
		this.pageSizeByTableKey.set(nextPageSizes);
		this.localStorageService.setCmsTablePageSizes(nextPageSizes);
	}

	public getSelectedPageIndex(tableKey: string): number | undefined {
		return this.pageIndexByTableKey()[tableKey];
	}

	public setSelectedPageIndex(tableKey: string, pageIndex: number): void {
		const allPageIndexes = this.pageIndexByTableKey();

		if (allPageIndexes[tableKey] === pageIndex) {
			return;
		}

		this.pageIndexByTableKey.set({
			...allPageIndexes,
			[tableKey]: pageIndex,
		});
	}

	public clearSelectedPageIndex(tableKey: string): void {
		const allPageIndexes = this.pageIndexByTableKey();

		if (!(tableKey in allPageIndexes)) {
			return;
		}

		const nextPageIndexes = { ...allPageIndexes };
		delete nextPageIndexes[tableKey];
		this.pageIndexByTableKey.set(nextPageIndexes);
	}

	private cloneFilters(filters: CmsTableStoredFilters): CmsTableStoredFilters {
		const clone: CmsTableStoredFilters = {};

		Object.keys(filters).forEach(key => {
			clone[key] = [...filters[key]];
		});

		return clone;
	}
}
